import { zodResolver } from "@hookform/resolvers/zod";
import React from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { FaPlus } from "react-icons/fa";
import { z } from "zod";

import * as api from "../../api/api";
import { useOrganizationDocument } from "../../api/firestoreWrappers.generated.jsx";
import { useUser } from "../../contexts/user_provider";
import CloudConnectionSettingsFormFields, {
    CloudConnectionSettingsSchema,
} from "../AppSettings/CloudConnectionSettings/index.jsx";

export default function AddExportModal({
    organizationId,
    locationId,
    mission,
    show,
    hide,
}) {
    const { getUserIsAdmin } = useUser();
    const [organization] = useOrganizationDocument(organizationId, {
        mode: "once",
    });
    const [corvusOrganization] = useOrganizationDocument(
        api.CORVUS_ORGANIZATION_ID,
        { mode: "once" },
    );

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { errors, isSubmitting, isDirty },
    } = useForm({
        resolver: zodResolver(
            z.object({
                settings: z.object({
                    ...CloudConnectionSettingsSchema.shape,
                }),
            }),
        ),
        defaultValues: {
            settings: {
                cloud_connection: [],
            },
        },
    });

    const cloudConnections = Object.entries(
        organization?.cloud_connection?.connections ?? {},
    ).map(([key, value]) => ({
        id: key,
        name: value,
    }));
    const corvusCloudConnections = Object.entries(
        corvusOrganization?.cloud_connection?.connections ?? {},
    ).map(([key, value]) => ({
        id: key,
        name: value,
    }));

    const cloudConnectionIds = new Set(cloudConnections.map((c) => c.id));

    const allCloudConnections = [
        ...cloudConnections,
        ...corvusCloudConnections.filter((c) => !cloudConnectionIds.has(c.id)),
    ];

    React.useEffect(() => {
        if (!mission) return;

        reset({
            settings: {
                cloud_connection:
                    mission.apps?.[0]?.settings?.cloud_connection ?? [],
            },
        });
    }, [mission, reset]);

    async function onSubmit(values) {
        if (!mission) return;

        await api.updateMissionCloudExports(
            organizationId,
            locationId,
            mission.id,
            values.settings.cloud_connection ?? [],
        );

        hide();
    }

    if (!getUserIsAdmin()) return null;

    return (
        <Modal show={show} onHide={hide}>
            <Form onSubmit={handleSubmit(onSubmit)}>
                <Modal.Header closeButton>
                    <Modal.Title>Add cloud exports</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <CloudConnectionSettingsFormFields
                        control={control}
                        errors={errors}
                        setValue={setValue}
                        possibleConnections={allCloudConnections}
                    />
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={hide}>
                        Cancel
                    </Button>

                    <Button
                        variant="primary"
                        type="submit"
                        disabled={isSubmitting || !isDirty}
                    >
                        <FaPlus
                            className="me-2"
                            style={{ marginTop: "-2px" }}
                        />
                        {isSubmitting ? "Adding..." : "Add"}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}
